import { useState, Fragment } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import PauseIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { SeoCopilotSession, SeoCopilotCycle } from '@/types/api/seo';
import { PageHeader } from '@/components/shared';
import MetricCard from '@/trio-ui/MetricCard';

type Props = {
  sessions: SeoCopilotSession[];
  cyclesMap: Record<string, SeoCopilotCycle[]>;
  isLoading: boolean;
  onPause: (sessionId: string) => void;
  onResume: (sessionId: string) => void;
  onLoadCycles: (sessionId: string) => void;
};

const statusColor: Record<string, string> = {
  active: '#054AD9',
  paused: '#F4B400',
  completed: '#0D9D58',
  failed: '#EF5B5B',
};

const statusChipColor: Record<string, 'info' | 'warning' | 'success' | 'error' | 'default'> = {
  active: 'info',
  paused: 'warning',
  completed: 'success',
  failed: 'error',
};

const actionLabels: Record<string, string> = {
  title_tag: 'Title Tag',
  meta_description: 'Meta Description',
  content: 'Content',
  schema: 'Schema Markup',
  internal_link: 'Internal Link',
  none: 'No Action',
};

const SeoCopilotComponent = function ({
  sessions,
  cyclesMap,
  isLoading,
  onPause,
  onResume,
  onLoadCycles,
}: Props): JSX.Element {
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);

  const activeSessions = sessions.filter((s) => s.status === 'active');
  const completedSessions = sessions.filter((s) => s.status === 'completed');
  const improvedSessions = sessions.filter((s) =>
    s.startingPosition != null && s.currentPosition != null && s.currentPosition < s.startingPosition
  );
  const avgImprovement = improvedSessions.length > 0
    ? improvedSessions.reduce((sum, s) => sum + ((s.startingPosition ?? 0) - (s.currentPosition ?? 0)), 0) / improvedSessions.length
    : 0;

  const handleToggleSession = (sessionId: string) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
    } else {
      setExpandedSessionId(sessionId);
      onLoadCycles(sessionId);
    }
  };

  return (
    <Box sx={{}}>
      <PageHeader title="SEO Copilot" />

      {/* Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
        <MetricCard label="Active Sessions" value={activeSessions.length} color="#054AD9" />
        <MetricCard label="Keywords Improved" value={improvedSessions.length} color="#0D9D58" />
        <MetricCard label="Avg. Position Gain" value={avgImprovement > 0 ? `+${avgImprovement.toFixed(1)}` : '—'} color="#0D9D58" />
        <MetricCard label="Goals Reached" value={completedSessions.length} color="#054AD9" />
      </Box>

      {/* Description */}
      {sessions.length === 0 && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>No copilot sessions yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Go to a keyword detail page and click "Activate Copilot" to start autonomous optimization.
          </Typography>
        </Box>
      )}

      {/* Sessions */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sessions.map((session) => {
          const isExpanded = expandedSessionId === session.id;
          const cycles = cyclesMap[session.id] ?? [];
          const posImproved = session.startingPosition != null && session.currentPosition != null && session.currentPosition < session.startingPosition;
          const posDiff = session.startingPosition != null && session.currentPosition != null
            ? session.startingPosition - session.currentPosition : 0;

          return (
            <Box
              key={session.id}
              sx={{ border: '2px solid #F4F4F5', borderRadius: '12px', overflow: 'hidden' }}
            >
              {/* Session header — clickable to expand */}
              <Box
                onClick={() => handleToggleSession(session.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2,
                  cursor: 'pointer', transition: 'background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                  '&:hover': { backgroundColor: '#F4F4F5' },
                }}
              >
                {/* Status dot */}
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: statusColor[session.status] ?? '#C5C5C7',
                  ...(session.status === 'active' ? { animation: 'pulse 2s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } } : {}),
                }} />

                {/* Keyword */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {session.keyword?.keyword ?? session.keywordId.slice(0, 8)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {session.totalCycles} cycle{session.totalCycles !== 1 ? 's' : ''} — next: {session.nextCycleAt ? new Date(session.nextCycleAt).toLocaleDateString() : 'none'}
                  </Typography>
                </Box>

                {/* Position progress */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                  <Typography variant="body2" color="text.secondary">#{session.startingPosition?.toFixed(1) ?? '?'}</Typography>
                  <Typography variant="caption" color="text.disabled">→</Typography>
                  <Typography variant="body2" fontWeight={500} color={posImproved ? 'success.main' : 'text.primary'}>
                    #{session.currentPosition?.toFixed(1) ?? '?'}
                  </Typography>
                  {posDiff > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: 'success.main' }}>
                      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={500}>{posDiff.toFixed(1)}</Typography>
                    </Box>
                  )}
                </Box>

                {/* Target */}
                <Chip label={`Top ${session.targetPosition}`} size="small" sx={{ fontSize: 11, flexShrink: 0 }} />

                {/* Status */}
                <Chip label={session.status} color={statusChipColor[session.status]} size="small" sx={{ fontSize: 11, textTransform: 'capitalize', flexShrink: 0 }} />

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {session.status === 'active' && (
                    <Button size="small" onClick={(e) => { e.stopPropagation(); onPause(session.id); }} sx={{ fontSize: 12, minWidth: 'auto', p: '2px 8px', color: '#71717A' }}>
                      Pause
                    </Button>
                  )}
                  {session.status === 'paused' && (
                    <Button size="small" onClick={(e) => { e.stopPropagation(); onResume(session.id); }} sx={{ fontSize: 12, minWidth: 'auto', p: '2px 8px', color: '#054AD9' }}>
                      Resume
                    </Button>
                  )}
                </Box>

                {isExpanded ? <ExpandLessIcon sx={{ fontSize: 20, color: '#71717A' }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: '#71717A' }} />}
              </Box>

              {/* Expanded cycle timeline */}
              {isExpanded && (
                <Box sx={{ borderTop: '1px solid hsla(0,0%,0%,0.08)', px: 3, py: 2 }}>
                  {!cyclesMap[session.id] ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Loading cycles...
                    </Typography>
                  ) : cycles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No cycles yet. The first cycle will run shortly after activation. If this persists, the analysis may have failed — check that the seo-copilot and seo-analysis functions are deployed.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {cycles.map((cycle, i) => {
                        const isLast = i === 0; // cycles are ordered desc
                        const improved = cycle.positionAfter != null && cycle.positionBefore != null && cycle.positionAfter < cycle.positionBefore;
                        const declined = cycle.positionAfter != null && cycle.positionBefore != null && cycle.positionAfter > cycle.positionBefore;

                        return (
                          <Box key={cycle.id} sx={{ display: 'flex', gap: 2, pb: 2, mb: i < cycles.length - 1 ? 2 : 0, borderBottom: i < cycles.length - 1 ? '1px solid hsla(0,0%,0%,0.04)' : 'none' }}>
                            {/* Timeline indicator */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                              {cycle.status === 'completed' ? (
                                <CheckCircleIcon sx={{ fontSize: 20, color: '#054AD9' }} />
                              ) : cycle.status === 'failed' ? (
                                <ErrorIcon sx={{ fontSize: 20, color: '#EF5B5B' }} />
                              ) : cycle.status === 'skipped' ? (
                                <ScheduleIcon sx={{ fontSize: 20, color: '#F4B400' }} />
                              ) : (
                                <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #C5C5C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#C5C5C7' }} />
                                </Box>
                              )}
                              {i < cycles.length - 1 && (
                                <Box sx={{ width: 2, flex: 1, backgroundColor: '#F4F4F5', mt: 0.5 }} />
                              )}
                            </Box>

                            {/* Cycle content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* Header */}
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight={500}>Cycle {cycle.cycleNumber}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(cycle.createdAt).toLocaleDateString()}
                                  </Typography>
                                </Box>
                                {/* Position change */}
                                {cycle.positionBefore != null && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography variant="caption" color="text.secondary">#{cycle.positionBefore.toFixed(1)}</Typography>
                                    {cycle.positionAfter != null && (
                                      <>
                                        <Typography variant="caption" color="text.disabled">→</Typography>
                                        <Typography variant="caption" fontWeight={500} color={improved ? 'success.main' : declined ? 'error.main' : 'text.primary'}>
                                          #{cycle.positionAfter.toFixed(1)}
                                        </Typography>
                                        {improved && <ArrowUpwardIcon sx={{ fontSize: 12, color: 'success.main' }} />}
                                        {declined && <ArrowDownwardIcon sx={{ fontSize: 12, color: 'error.main' }} />}
                                      </>
                                    )}
                                    {cycle.impactStatus === 'pending' && (
                                      <Chip label="Pending" size="small" sx={{ fontSize: 10, height: 18, backgroundColor: '#F4F4F5' }} />
                                    )}
                                  </Box>
                                )}
                              </Box>

                              {/* Diagnosis */}
                              {cycle.diagnosisReasoning && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, lineHeight: '18px' }}>
                                  {cycle.diagnosisReasoning}
                                </Typography>
                              )}

                              {/* Action taken */}
                              {cycle.actionTaken && cycle.actionTaken !== 'none' && (
                                <Box sx={{ backgroundColor: '#F4F4F5', borderRadius: '8px', p: 1.5, mb: 0.75 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip label={actionLabels[cycle.actionTaken] ?? cycle.actionTaken} size="small" sx={{ fontSize: 10, height: 18, backgroundColor: '#054AD920', color: '#054AD9' }} />
                                    {cycle.selectedOpportunityTitle && (
                                      <Typography variant="caption" fontWeight={500}>{cycle.selectedOpportunityTitle}</Typography>
                                    )}
                                  </Box>
                                  {cycle.actionDescription && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: '18px', whiteSpace: 'pre-line' }}>
                                      {cycle.actionDescription}
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {/* Error */}
                              {cycle.errorMessage && (
                                <Box sx={{ backgroundColor: '#EF5B5B10', borderRadius: '8px', p: 1.5 }}>
                                  <Typography variant="caption" color="error.main">
                                    {cycle.errorMessage}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SeoCopilotComponent;
